import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { isExamRoute } from '../../shared/lib/exam-routes';
import { useServiceWorkerUpdate } from '../../shared/pwa/useServiceWorkerUpdate';
import { useToast } from '../../shared/ui/toast-context';

/**
 * "A new version is ready" — as a sticky toast with one action, never a
 * forced reload.
 *
 * Headless: it owns a toast rather than rendering one. Hidden on exam routes
 * (a reload there would cost a candidate their place) and shown again on the
 * way out, because hidden is not dismissed; a toast the user swiped away
 * stays away until the next build.
 */
export function UpdateToast(): null {
  const { updateReady, applyUpdate } = useServiceWorkerUpdate();
  const { pathname } = useLocation();
  const { show, dismiss } = useToast();
  const onExam = isExamRoute(pathname);
  const userDismissed = useRef(false);
  const shownId = useRef<string | null>(null);

  useEffect(() => {
    if (!updateReady || onExam || userDismissed.current) {
      if (shownId.current !== null) {
        dismiss(shownId.current);
        shownId.current = null;
      }
      return;
    }
    if (shownId.current !== null) return;
    shownId.current = show('A new version is ready.', {
      tone: 'info',
      duration: null,
      key: 'sw-update',
      action: { label: 'Reload', onClick: applyUpdate },
      onDismiss: (reason) => {
        shownId.current = null;
        if (reason === 'user') userDismissed.current = true;
      },
    });
  }, [updateReady, onExam, show, dismiss, applyUpdate]);

  return null;
}
